import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
} from "@mui/material";
import { Edit, Save, Delete, Cancel } from "@mui/icons-material";
import Button from "../../../common/Button";

const UserList = ({
  csvData,
  handleSaveRow,
  editingRow,
  setEditingRow,
  handleEditRow,
  handleBackStep,
  handleSubmit,
  handleDeleteRow,
  availableGroups = [],
  availableUserTypes = [],
  findUserTypeId,
  findGroupId,
  loadUpload,
}) => {
  // Local state to store temporary edits
  const [tempRowData, setTempRowData] = useState({});

  // Column widths state for resizing
  const [columnWidths, setColumnWidths] = useState({
    fullName: 50,
    email: 150,
    type: 80,
    group: 80,
    actions: 80,
  });

  const userTypeOptions =
    availableUserTypes.length > 0
      ? availableUserTypes.map((type) => type.title)
      : ["Admin", "Editor", "Content Creator", "Reader"];

  console.log("UserList - Available user types:", availableUserTypes);
  console.log("UserList - User type options:", userTypeOptions);

  // Check if a row has missing required information
  const hasError = (user) => {
    const fullName = user["Full Name"] || user.fullName || "";
    const email = user["Email"] || user.email || "";
    const type = user["Type"] || user.type || "";

    return !fullName.trim() || !email.trim() || !type.trim();
  };

  // Check if any user has errors
  const hasAnyErrors = csvData.some(hasError);

  // Additional validation: check if user types are valid
  const hasInvalidUserTypes = csvData.some((user) => {
    const typeName = user["Type"] || user.type || "";
    if (!typeName.trim()) return true;

    const typeId = findUserTypeId ? findUserTypeId(typeName) : null;
    return typeId === null;
  });

  const allErrors = hasAnyErrors || hasInvalidUserTypes;

  // Handle field changes in edit mode (store in temp state)
  const handleFieldChange = (index, field, value) => {
    setTempRowData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
      },
    }));
  };

  // Save the edited row
  const handleSaveEdit = (index) => {
    const tempData = tempRowData[index];
    if (tempData) {
      // Apply all changes at once
      Object.keys(tempData).forEach((field) => {
        handleSaveRow(index, field, tempData[field]);
      });
    }
    setEditingRow(null);
    setTempRowData((prev) => {
      const newData = { ...prev };
      delete newData[index];
      return newData;
    });
  };

  // Cancel editing
  const handleCancelEdit = (index) => {
    setEditingRow(null);
    setTempRowData((prev) => {
      const newData = { ...prev };
      delete newData[index];
      return newData;
    });
  };

  // Start editing
  const handleStartEdit = (index) => {
    const user = csvData[index];
    setTempRowData((prev) => ({
      ...prev,
      [index]: {
        "Full Name": getFieldValue(user, "fullName"),
        Email: getFieldValue(user, "email"),
        Type: getFieldValue(user, "type"),
        Group: getFieldValue(user, "group"),
      },
    }));
    handleEditRow(index);
  };

  const getFieldValue = (user, field) => {
    switch (field) {
      case "fullName":
        return user["Full Name"] || user.fullName || "";
      case "email":
        return user["Email"] || user.email || "";
      case "type":
        return user["Type"] || user.type || "";
      case "group":
        return user["Group"] || user.group || "";
      default:
        return "";
    }
  };

  // Get field value for editing (from temp state or original data)
  const getEditFieldValue = (user, index, field) => {
    const tempData = tempRowData[index];
    if (tempData && tempData[field] !== undefined) {
      return tempData[field];
    }
    return getFieldValue(user, field);
  };

  const isUserTypeValid = (user) => {
    const typeName = user["Type"] || user.type || "";
    if (!typeName.trim()) return false;

    const typeId = findUserTypeId ? findUserTypeId(typeName) : null;
    return typeId !== null;
  };

  // Handle column resizing
  const handleMouseDown = (columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];

    const handleMouseMove = (e) => {
      const newWidth = Math.max(100, startWidth + (e.clientX - startX));
      setColumnWidths((prev) => ({
        ...prev,
        [columnKey]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Resizer component
  const ColumnResizer = ({ columnKey }) => (
    <div
      onMouseDown={(e) => handleMouseDown(columnKey, e)}
      style={{
        position: "absolute",
        top: 0,
        right: -2,
        width: 8,
        height: "100%",
        cursor: "col-resize",
        backgroundColor: "rgba(25, 118, 210, 0.1)",
        border: "1px solid transparent",
        zIndex: 10,
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = "rgba(25, 118, 210, 0.3)";
        e.target.style.borderColor = "#1976d2";
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = "rgba(25, 118, 210, 0.1)";
        e.target.style.borderColor = "transparent";
      }}
    />
  );

  return (
    <div>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        Step 2: Review Users
      </Typography>

      {hasAnyErrors && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "#fff3cd",
            borderRadius: 1,
            border: "1px solid #ffeaa7",
          }}
        >
          <Typography variant="body2" color="error">
            Some users are missing required information (Full Name, Email, or
            Type). Please correct these before submitting.
          </Typography>
        </Box>
      )}

      {hasInvalidUserTypes && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "#ffebee",
            borderRadius: 1,
            border: "1px solid #ffcdd2",
          }}
        >
          <Typography variant="body2" color="error">
            Some users have invalid user types that don't match the available
            options. Please check the Type column and ensure they match exactly.
          </Typography>
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{ maxHeight: 400, mb: 2, overflow: "auto" }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: columnWidths.fullName,
                  minWidth: columnWidths.fullName,
                  maxWidth: columnWidths.fullName,
                  position: "relative",
                  borderRight: "1px solid #e0e0e0",
                  padding: "8px 12px",
                  overflow: "hidden",
                }}
              >
                <strong>Full Name</strong>
                <ColumnResizer columnKey="fullName" />
              </TableCell>
              <TableCell
                sx={{
                  width: columnWidths.email,
                  minWidth: columnWidths.email,
                  maxWidth: columnWidths.email,
                  position: "relative",
                  borderRight: "1px solid #e0e0e0",
                  padding: "8px 12px",
                  overflow: "hidden",
                }}
              >
                <strong>Email</strong>
                <ColumnResizer columnKey="email" />
              </TableCell>
              <TableCell
                sx={{
                  width: columnWidths.type,
                  minWidth: columnWidths.type,
                  maxWidth: columnWidths.type,
                  position: "relative",
                  borderRight: "1px solid #e0e0e0",
                  padding: "8px 12px",
                  overflow: "hidden",
                }}
              >
                <strong>Type</strong>
                <ColumnResizer columnKey="type" />
              </TableCell>
              <TableCell
                sx={{
                  width: columnWidths.group,
                  minWidth: columnWidths.group,
                  maxWidth: columnWidths.group,
                  position: "relative",
                  borderRight: "1px solid #e0e0e0",
                  padding: "8px 12px",
                  overflow: "hidden",
                }}
              >
                <strong>Group</strong>
                <ColumnResizer columnKey="group" />
              </TableCell>
              <TableCell
                sx={{
                  width: columnWidths.actions,
                  minWidth: columnWidths.actions,
                  maxWidth: columnWidths.actions,
                  position: "relative",
                  padding: "8px 12px",
                  overflow: "hidden",
                }}
              >
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {csvData.map((user, index) => {
              const isEditing = editingRow === index;
              const hasRowError = hasError(user);
              const hasInvalidType = !isUserTypeValid(user);
              const hasAnyRowError = hasRowError || hasInvalidType;

              return (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: hasAnyRowError ? "#ffebee" : "inherit",
                    borderLeft: hasAnyRowError ? "3px solid #f44336" : "none",
                  }}
                >
                  <TableCell
                    sx={{
                      width: columnWidths.fullName,
                      minWidth: columnWidths.fullName,
                      maxWidth: columnWidths.fullName,
                      padding: "8px 12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={getEditFieldValue(user, index, "Full Name")}
                        onChange={(e) =>
                          handleFieldChange(index, "Full Name", e.target.value)
                        }
                        error={
                          !getEditFieldValue(user, index, "Full Name").trim()
                        }
                        fullWidth
                        placeholder="Enter full name"
                      />
                    ) : (
                      <span
                        style={{
                          color: !getFieldValue(user, "fullName").trim()
                            ? "#f44336"
                            : "inherit",
                          fontWeight: !getFieldValue(user, "fullName").trim()
                            ? "bold"
                            : "normal",
                        }}
                        title={
                          getFieldValue(user, "fullName").trim() || "Missing"
                        }
                      >
                        {getFieldValue(user, "fullName").trim() || "Missing"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: columnWidths.email,
                      minWidth: columnWidths.email,
                      maxWidth: columnWidths.email,
                      padding: "8px 12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="email"
                        value={getEditFieldValue(user, index, "Email")}
                        onChange={(e) =>
                          handleFieldChange(index, "Email", e.target.value)
                        }
                        error={!getEditFieldValue(user, index, "Email").trim()}
                        fullWidth
                        placeholder="Enter email address"
                      />
                    ) : (
                      <span
                        style={{
                          color: !getFieldValue(user, "email").trim()
                            ? "#f44336"
                            : "inherit",
                          fontWeight: !getFieldValue(user, "email").trim()
                            ? "bold"
                            : "normal",
                        }}
                        title={getFieldValue(user, "email").trim() || "Missing"}
                      >
                        {getFieldValue(user, "email").trim() || "Missing"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: columnWidths.type,
                      minWidth: columnWidths.type,
                      maxWidth: columnWidths.type,
                      padding: "8px 12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isEditing ? (
                      <FormControl
                        size="small"
                        fullWidth
                        error={!getEditFieldValue(user, index, "Type").trim()}
                      >
                        <Select
                          value={getEditFieldValue(user, index, "Type")}
                          onChange={(e) =>
                            handleFieldChange(index, "Type", e.target.value)
                          }
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select Type</em>
                          </MenuItem>
                          {userTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <span
                        style={{
                          color:
                            !getFieldValue(user, "type").trim() ||
                            hasInvalidType
                              ? "#f44336"
                              : "inherit",
                          fontWeight:
                            !getFieldValue(user, "type").trim() ||
                            hasInvalidType
                              ? "bold"
                              : "normal",
                        }}
                        title={getFieldValue(user, "type").trim() || "Missing"}
                      >
                        {getFieldValue(user, "type").trim() || "Missing"}
                        {hasInvalidType &&
                          getFieldValue(user, "type").trim() && (
                            <span
                              style={{ fontSize: "0.75em", display: "block" }}
                            >
                              (Invalid type)
                            </span>
                          )}
                      </span>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: columnWidths.group,
                      minWidth: columnWidths.group,
                      maxWidth: columnWidths.group,
                      padding: "8px 12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isEditing ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={getEditFieldValue(user, index, "Group")}
                          onChange={(e) =>
                            handleFieldChange(index, "Group", e.target.value)
                          }
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {availableGroups.map((group) => (
                            <MenuItem key={group.id} value={group.name}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <span
                        style={{
                          color: "#666",
                          fontStyle: getFieldValue(user, "group")
                            ? "normal"
                            : "italic",
                        }}
                        title={getFieldValue(user, "group") || "None"}
                      >
                        {getFieldValue(user, "group") || "None"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: columnWidths.actions,
                      minWidth: columnWidths.actions,
                      maxWidth: columnWidths.actions,
                      padding: "8px 12px",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ display: "flex", gap: 4 }}>
                      {isEditing ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(index)}
                            color="primary"
                            title="Save changes"
                          >
                            <Save />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCancelEdit(index)}
                            color="secondary"
                            title="Cancel editing"
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(index)}
                          color="primary"
                          title="Edit user"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(index)}
                        color="error"
                        title="Delete user"
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Button
          onClick={handleSubmit}
          disabled={allErrors || csvData.length === 0 || loadUpload}
          text={`Submit ${csvData.length} User${
            csvData.length !== 1 ? "s" : ""
          }`}
          isLoading={loadUpload}
        />
        <Button onClick={handleBackStep} text="Back" backgroundColor="white" />
      </div>
    </div>
  );
};

export default UserList;
